// Theme Manager Hook for Landing Page

import { useState, useCallback, useMemo, useEffect } from "react";

import { PaletteMode } from "@mui/material";

import { STORAGE_KEYS } from "@rusha/shared-theme";

/**
 * Get system theme preference
 */
const getSystemTheme = (): PaletteMode => {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

/**
 * Theme management hook for landing page
 * Supports system preference detection and localStorage persistence
 */
export const useThemeManager = () => {
  // Initialize theme mode from localStorage, system preference, or default to 'light'
  const [mode, setMode] = useState<PaletteMode>(() => {
    const savedMode = localStorage.getItem(STORAGE_KEYS.THEME_MODE);
    if (savedMode === "light" || savedMode === "dark") {
      return savedMode;
    }
    return getSystemTheme();
  });

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      const savedMode = localStorage.getItem(STORAGE_KEYS.THEME_MODE);
      if (!savedMode) {
        setMode(e.matches ? "dark" : "light");
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  const toggleColorMode = useCallback(() => {
    const newMode = mode === "light" ? "dark" : "light";
    setMode(newMode);
    localStorage.setItem(STORAGE_KEYS.THEME_MODE, newMode);
  }, [mode]);

  const setColorMode = useCallback((newMode: PaletteMode) => {
    setMode(newMode);
    localStorage.setItem(STORAGE_KEYS.THEME_MODE, newMode);
  }, []);

  const themeInfo = useMemo(
    () => ({
      mode,
      toggleColorMode,
      setColorMode,
      isDark: mode === "dark",
      isLight: mode === "light",
    }),
    [mode, toggleColorMode, setColorMode],
  );

  return themeInfo;
};
